import Divider from "@/components/shared/Divider/Divider";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PRIMARY_COLOR } from "../constants";

interface HeaderTooltipProps {
  title: string;
  description: string;
  example?: string;
}

export function HeaderTooltip({ title, description, example }: HeaderTooltipProps) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="ml-1 inline-flex items-center shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="z-[9999] w-[320px] bg-zinc-900 border border-colorBorder p-4 shadow-2xl rounded-lg"
        >
          <div className="flex flex-col gap-2">
            <p className="font-semibold text-colorText text-sm leading-normal break-words whitespace-normal">
              {title}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed break-words whitespace-normal">
              {description}
            </p>
            {example && (
              <div className="border-t border-colorBorder pt-2 mt-1">
                <p className="text-[11px] text-primary/90 leading-relaxed break-words whitespace-normal">
                  <span className="font-medium">Exemplo:</span> {example}
                </p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ChartHeaderProps {
  title: string;
  description: string;
}

export function ChartHeader({ title, description }: ChartHeaderProps) {
  return (
    <div className="px-4 py-4">
      <h3 className="text-sm font-semibold text-colorText">{title}</h3>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
  );
}

export function CustomChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-zinc-900 border border-colorBorder px-3 py-2 rounded-lg shadow-xl z-50">
      <p className="text-sm font-medium text-colorText mb-1">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 text-xs">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color || PRIMARY_COLOR }}
          />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="text-colorText font-medium">
            {entry.payload?.amount_formatted || entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function ChartEmpty({ className }: { className: string }) {
  const { t } = useTranslation("reports");

  return (
    <div className={cn("flex items-center justify-center text-muted-foreground text-sm", className)}>
      {t("reports:no_data_for_period")}
    </div>
  );
}

interface ChartCardProps {
  title: string;
  description: string;
  isLoading: boolean;
  isEmpty: boolean;
  /** Altura do skeleton e do estado vazio, ex.: "h-[200px]". */
  heightClassName: string;
  children: ReactNode;
}

/** Card padrão das seções: cabeçalho, divisor e corpo com carregando/vazio/dados. */
export function ChartCard({
  title,
  description,
  isLoading,
  isEmpty,
  heightClassName,
  children,
}: ChartCardProps) {
  return (
    <Card className="overflow-hidden">
      <ChartHeader title={title} description={description} />
      <Divider />
      <div className="p-4">
        {isLoading ? (
          <Skeleton className={cn("w-full", heightClassName)} />
        ) : isEmpty ? (
          <ChartEmpty className={heightClassName} />
        ) : (
          children
        )}
      </div>
    </Card>
  );
}
